import React, { useState } from 'react';
import { 
  QrCode, 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle2, 
  Upload, 
  Camera, 
  ExternalLink,
  Info
} from 'lucide-react';

export const QrScannerPage: React.FC = () => {
  const [scannedUrl, setScannedUrl] = useState('');
  const [analysis, setAnalysis] = useState<any | null>(null);

  const sampleScans = [
    { label: 'Fake Parking Meter (Quishing)', url: 'https://pay-parking-meter-sydney-au.net/checkout?bay=409' },
    { label: 'Genuine Service NSW QR Check-in', url: 'https://www.service.nsw.gov.au/campaign/service-nsw-mobile-app' },
    { label: 'Restaurant Menu QR (Typosquat)', url: 'https://menu-ordering-bistro-sydney.xyz/pay' }
  ];

  const handleAnalyzeUrl = (url: string) => {
    setScannedUrl(url);
    const isQuish = url.includes('pay-parking') || url.includes('.xyz');
    const isGov = url.includes('.gov.au');

    if (isQuish) {
      setAnalysis({
        url,
        domain: url.split('/')[2],
        isMalicious: true,
        riskScore: 94,
        threatType: 'Quishing / Credit Card Phishing',
        warningSigns: [
          'Deceptive domain mimicking municipal council parking payment systems.',
          'Non-official .net top-level domain for Australian public infrastructure.',
          'Requests upfront credit card entry for tiny parking fees ($3.50).'
        ],
        safeAction: 'Do not open this URL on your phone or enter payment details. Pay via official parking meters.'
      });
    } else if (isGov) {
      setAnalysis({
        url,
        domain: url.split('/')[2],
        isMalicious: false,
        riskScore: 2,
        threatType: 'Verified Official Government Link',
        warningSigns: [],
        safeAction: 'Safe to open. Verified official NSW Government web domain (.gov.au).'
      });
    } else {
      setAnalysis({
        url,
        domain: url.split('/')[2],
        isMalicious: false,
        riskScore: 24,
        threatType: 'Standard Commercial URL',
        warningSigns: [],
        safeAction: 'Standard web link. Ensure URL matches the venue before completing payments.'
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-bold border border-emerald-800">
          <QrCode className="w-3.5 h-3.5" />
          <span>IN-BROWSER QR & QUISHING THREAT SCANNER</span>
        </div>
        <h1 className="text-3xl font-extrabold text-white">QR Code Security & Scam Inspector</h1>
        <p className="text-xs text-slate-400 max-w-xl mx-auto">
          Scan or paste QR code destinations before opening them. Detect malicious parking meter stickers, typosquatted restaurant menus, and credential harvesters.
        </p>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl text-xs">
        <div className="space-y-2">
          <label className="block font-bold text-slate-300">Paste Decoded QR Code URL to Inspect:</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={scannedUrl}
              onChange={(e) => setScannedUrl(e.target.value)}
              placeholder="https://..."
              className="flex-1 px-3 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono focus:outline-none focus:border-emerald-500"
            />
            <button
              onClick={() => handleAnalyzeUrl(scannedUrl)}
              className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold shadow-lg"
            >
              Analyze URL
            </button>
          </div>
        </div>

        <div className="pt-2 border-t border-slate-800 flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-slate-400">Sample Scenarios:</span>
          {sampleScans.map((s, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleAnalyzeUrl(s.url)}
              className="px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 text-slate-300 hover:border-slate-700"
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {analysis && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl animate-fade-in text-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-4">
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-mono">Decoded Destination</span>
              <div className="text-base font-bold text-white font-mono break-all">{analysis.url}</div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-bold border ${
              analysis.isMalicious ? 'bg-rose-950 text-rose-300 border-rose-800' : 'bg-emerald-950 text-emerald-300 border-emerald-800'
            }`}>
              {analysis.threatType}
            </span>
          </div>

          {analysis.warningSigns.length > 0 && (
            <div className="space-y-2 p-4 rounded-xl bg-rose-950/20 border border-rose-800 text-rose-200">
              <div className="font-bold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-400" />
                <span>Detected Quishing Indicators:</span>
              </div>
              <ul className="list-disc pl-5 space-y-1">
                {analysis.warningSigns.map((w: string, i: number) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-1">
            <span className="font-bold text-white">Safe Recommendation:</span>
            <p className="text-slate-300">{analysis.safeAction}</p>
          </div>
        </div>
      )}
    </div>
  );
};
